// Activity feed for an inquiry — renders the append-only audit timeline from
// GET :id/history (header + item status changes, assignment events). Unknown
// eventType values (future phases) render generically instead of breaking.

import { Badge, Box, Button, Card, CardBody, Center, Flex, Spinner, Text } from '@elkaso-app/web-design';
import {
  RestaurantInquiryEvent,
  RestaurantInquiryItem,
  useGetRestaurantInquiryHistoryApi,
} from 'apis/restaurant-inquiries';
import { fmtDT } from 'pages/inquiry-processor/utils/scoring';
import { rawStatusLabel } from 'pages/inquiry-processor/utils/status';
import { useState } from 'react';

interface Props {
  inquiryId: string;
  items: RestaurantInquiryItem[];
}

const PAGE_SIZE = 20;

// One-line summary per event. History doesn't join masterProduct, so item
// names resolve from the already-loaded items list (fallback: #entityId).
const eventText = (ev: RestaurantInquiryEvent, items: RestaurantInquiryItem[]): string => {
  const itemName = (id: number) => {
    const it = items.find((i) => i.id === id);
    return it?.masterProduct?.name || `#${id}`;
  };
  const quoteScope = (id: number) => {
    // Resolve the quote's supplier from the loaded items' nested quotes.
    for (const it of items) {
      const q = (it.quotes || []).find((x) => x.id === id);
      if (q) return `Quote by ${q.supplier?.name || `supplier #${q.supplierId}`} on “${itemName(it.id)}”`;
    }
    return `Quote #${id}`;
  };
  const scope =
    ev.entityType === 'item'
      ? `Item “${itemName(ev.entityId)}”`
      : ev.entityType === 'quote'
      ? quoteScope(ev.entityId)
      : 'Inquiry';

  if (ev.eventType === 'STATUS_CHANGED') {
    return `${scope}: ${rawStatusLabel(ev.fromValue)} → ${rawStatusLabel(ev.toValue)}`;
  }
  if (ev.eventType === 'ASSIGNED') {
    return `Assigned to ${ev.actor?.name || ev.actor?.email || `user #${ev.toValue}`}`;
  }
  // Future event types (QUOTED, FULFILLMENT_DECIDED, …) render generically.
  return `${scope}: ${ev.eventType}${ev.field ? ` (${ev.field})` : ''}${
    ev.fromValue || ev.toValue ? `: ${ev.fromValue ?? '—'} → ${ev.toValue ?? '—'}` : ''
  }`;
};

export const InquiryActivity = ({ inquiryId, items }: Props) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useGetRestaurantInquiryHistoryApi(inquiryId, page, PAGE_SIZE);

  const events = data?.data ?? [];

  return (
    <Card variant='outline' mt='4'>
      <CardBody>
        <Text
          fontSize='sm'
          fontWeight='semibold'
          color='gray.500'
          textTransform='uppercase'
          letterSpacing='wide'
          mb='2'>
          Activity
        </Text>
        {isLoading ? (
          <Center py='6'>
            <Spinner size='sm' color='red.500' />
          </Center>
        ) : isError ? (
          <Text fontSize='sm' color='gray.500'>
            Couldn’t load activity.
          </Text>
        ) : events.length === 0 ? (
          <Text fontSize='sm' color='gray.400'>
            No activity yet.
          </Text>
        ) : (
          <>
            {events.map((ev) => (
              <Box key={ev.id} py='2' borderBottom='1px solid' borderColor='gray.100' fontSize='sm'>
                <Flex justify='space-between' gap='3' wrap='wrap'>
                  <Flex align='center' gap='2' minW='0'>
                    <Badge colorScheme={ev.eventType === 'ASSIGNED' ? 'purple' : 'blue'} variant='subtle'>
                      {ev.eventType === 'STATUS_CHANGED'
                        ? 'Status'
                        : ev.eventType === 'ASSIGNED'
                        ? 'Assigned'
                        : ev.eventType}
                    </Badge>
                    <Text>{eventText(ev, items)}</Text>
                  </Flex>
                  <Text color='gray.500' whiteSpace='nowrap'>
                    {ev.actor?.name ? `${ev.actor.name} · ` : ''}
                    {fmtDT(new Date(ev.createdAt).getTime())}
                  </Text>
                </Flex>
                {ev.reason && (
                  <Text color='gray.500' fontStyle='italic' mt='0.5'>
                    “{ev.reason}”
                  </Text>
                )}
              </Box>
            ))}
            {(data?.hasPrev || data?.hasNext) && (
              <Flex gap='2' mt='3' justify='center'>
                <Button size='xs' variant='outline' isDisabled={!data?.hasPrev} onClick={() => setPage((p) => p - 1)}>
                  ← Newer
                </Button>
                <Button size='xs' variant='outline' isDisabled={!data?.hasNext} onClick={() => setPage((p) => p + 1)}>
                  Older →
                </Button>
              </Flex>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};
